/**
 * @fileoverview Connect a selected matter/client to explicit portal invitation creation and inspection. Backend context owns client/matter binding and invitation validity. Banking requests retain a stable request identity across retries. Invitation state and submitted identity/banking review are separate records; displaying a link does not establish client verification.
 */

import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AppIcon } from '../shared';
import { BANKING_REQUEST_CREATED } from './verificationTiers';
import './PortalInvitations.css';



export default function PortalInvitations({ matterNumber, clientId, clientName }) {
  const [context, setContext] = useState(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const [error, setError] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [linkKind, setLinkKind] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const requestId = useRef(null);
  const generation = useRef(0);
  useEffect(

  () => {
    let active = true;
    generation.current += 1;
    setContext(null); setLink(''); setError(''); setCopyStatus(''); setBusy(false); requestId.current = null;
    api.get(`/matters/${encodeURIComponent(matterNumber)}/portal/context`).then(({ data }) => {
      if (active) setContext(data);
    }).catch(() => { if (active) setContext(null); });
    return () => { active = false; generation.current += 1; };
  }, [matterNumber, clientId]);
  if (!context?.enabled || !context.clients?.some(client => String(client.id) === String(clientId))) return null;
  

  async function copyLink(url, current = generation.current) {
    try {
      await navigator.clipboard.writeText(url);
      if (generation.current === current) setCopyStatus('copied');
    } catch {
      if (generation.current === current) setCopyStatus('blocked');
    }
  }
  

  async function create(kind) {
    setBusy(true); setError(''); setLink(''); setCopyStatus(''); setExpiresAt(null);
    const current = generation.current;
    if (!requestId.current) requestId.current = crypto.randomUUID();
    try {
      const { data } = await api.post(`/matters/${encodeURIComponent(matterNumber)}/portal/${kind}-invitations`, {
        client_id: Number(clientId), ...(kind === 'banking' ? { request_id: requestId.current } : {}),
      });
      if (generation.current === current) {
        setLink(data.url); setLinkKind(kind); setExpiresAt(data.expires_at || null); requestId.current = null;
        if (kind === 'banking') window.dispatchEvent(new Event(BANKING_REQUEST_CREATED));
        await copyLink(data.url, current);
      }
    } catch (e) { if (generation.current === current) setError(e.response?.data?.error || 'The invitation could not be created. Please retry.'); }
    finally { if (generation.current === current) setBusy(false); }
  }
  return <section className="portal-invitations" aria-label={`Portal actions for ${clientName}`}>
    <div className="portal-invitations__actions">
      {context.banking_enabled !== false && <button type="button" disabled={busy || !clientId} onClick={() => create('banking')}>
        <AppIcon icon="pi pi-send" aria-hidden="true" />Request payment instructions
      </button>}
      <button type="button" disabled={busy || !clientId} onClick={() => create('upload')}>
        <AppIcon icon="pi pi-upload" aria-hidden="true" />Create upload link
      </button>
    </div>
    {link ? <div className="portal-invitations__link">
      <div className="portal-invitations__receipt">
        <span role="status" className="portal-invitations__status">
          <AppIcon icon={copyStatus === 'copied' ? 'pi pi-check' : 'pi pi-link'} aria-hidden="true" />
          {linkKind === 'banking' ? 'Payment' : 'Upload'} link {copyStatus === 'copied' ? 'copied' : 'ready'}
          {expiresAt ? <span> · Expires {new Date(expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span> : null}
        </span>
        <div className="portal-invitations__link-actions">
          <button type="button" aria-label="Copy link" title="Copy link" onClick={() => copyLink(link)}>
            <AppIcon icon="pi pi-copy" aria-hidden="true" />
          </button>
          <a href={link} target="_blank" rel="noreferrer" aria-label="Open client invitation" title="Open client invitation">
            <AppIcon icon="pi pi-external-link" aria-hidden="true" />
          </a>
        </div>
      </div>
      {copyStatus === 'blocked' ? <div className="portal-invitations__copy-fallback">
        <label htmlFor={`portal-link-${matterNumber}-${clientId}`}>Copying was blocked. Select the link to copy it.</label>
        <input id={`portal-link-${matterNumber}-${clientId}`} aria-label="Client invitation link" readOnly value={link} onFocus={(e) => e.target.select()} />
      </div> : null}
    </div> : null}
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}
